import React from 'react';
import { Box, Button, MenuItem, Select, Typography, useMediaQuery, useTheme } from '@mui/material';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import DownloadChart from './DownloadChart';
import { DEFAULT_PAGE_SIZE, DEFAULT_PAGE_NUMBER } from '@constants/constants';
interface PaginationProps {
  page?: number;
  size?: number;
  setSize?: (size: number) => void;
  total: number;
  handleChange: (page: number) => void;
  fileName: string;
  submoduleId: string;
  chartId: string;
  filterSelection: Record<string, string>;
  disablePagination?: boolean;
}

const PAGE_SIZE_OPTIONS = [
  {
    value: 10,
    label: '10',
  },
  {
    value: 25,
    label: '25',
  },
  {
    value: 50,
    label: '50',
  },
  {
    value: 100,
    label: '100',
  },
];

const Pagination: React.FC<PaginationProps> = (props) => {
  const {
    page = DEFAULT_PAGE_NUMBER,
    size = DEFAULT_PAGE_SIZE,
    total,
    handleChange,
    fileName,
    submoduleId,
    chartId,
    filterSelection,
    disablePagination = false,
    setSize,
  } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const uiPage = page + 1;

  const startRow = page * size + 1;
  const endRow = Math.min(uiPage * size, total);

  const isFirstPage = page === DEFAULT_PAGE_NUMBER;
  const isLastPage = page >= Math.ceil(total / size) - 1;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        mt: isMobile ? '1rem' : 0,
        justifyContent: isMobile ? 'flex-start' : 'flex-end',
        gap: '20px',
        width: isMobile ? '100%' : 'fit-content',
      }}
    >
      {submoduleId && (
        <DownloadChart
          fileName={fileName}
          submoduleId={submoduleId}
          chartId={chartId}
          filterSelection={filterSelection}
        />
      )}
      {setSize && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '0.875rem',
              lineHeight: '1.25rem',
              color: 'black',
            }}
          >
            Rows per page
          </Typography>
          <Select
            value={size}
            onChange={(e) => setSize(Number(e.target.value))}
            sx={{
              width: 'fit-content',
              borderRadius: '0.75rem',
              fontWeight: 500,
              fontSize: '0.875rem',
              lineHeight: '1.25rem',
              paddingLeft: '5px',
              '& .MuiSelect-select': {
                padding: '5px',
              },
            }}
            MenuProps={{
              sx: {
                zIndex: 10000,
              },
              PaperProps: {
                sx: {
                  maxHeight: '20rem',
                },
              },
            }}
          >
            {PAGE_SIZE_OPTIONS?.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </Box>
      )}
      {!disablePagination && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '0.875rem',
              lineHeight: '1.25rem',
              color: 'black',
            }}
          >
            {`${startRow}-${endRow} of ${total}`}
          </Typography>
          <Button
            sx={{ color: '#06164B', minWidth: '0px', padding: '0px' }}
            onClick={() => handleChange(page - 1)}
            disabled={isFirstPage}
          >
            <KeyboardArrowLeftIcon />
          </Button>
          <Button
            sx={{ color: '#06164B', minWidth: '0px', padding: '0px' }}
            onClick={() => handleChange(page + 1)}
            disabled={isLastPage}
          >
            <KeyboardArrowRightIcon />
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default Pagination;
